import clsx from 'clsx';
import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BenefitsSection from './components/BenefitsSection';
import BusinessGrowthSection from './components/BusinessGrowthSection';
import FeaturesSection from './components/FeaturesSection';
import Footer from './components/Footer';
import StickyHeader from './components/StickyHeader'; // Import the new StickyHeader component

import { showToast, postData } from '../../globalFunction';
import ForgotPassword from '../forgotPassword';

import './index.scss';

interface LoginState {
  username: string;
  password: string;
  rememberMe: boolean;
  disabled: boolean;
  forgotPasswordOpen: boolean;
}

interface RootState {
  login: LoginState;
}

const Auth = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isStickyHeaderVisible, setStickyHeaderVisible] = useState(false);

  const dispatch = useDispatch();
  const loginState = useSelector((store: RootState) => store.login);

  const checkInitialPath = useCallback(() => {
    const path = window.location.hash;
    if (path) {
      localStorage.setItem('path', path);
      window.history.replaceState({}, '', '/auth');
    }
  }, []);

  const checkRememberedUser = useCallback(() => {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    if (rememberMe) {
      const username = localStorage.getItem('username') || '';
      dispatch({ type: 'SET_LOGIN_DATA', name: 'username', value: username });
      dispatch({ type: 'SET_LOGIN_DATA', name: 'rememberMe', value: true });
      dispatch({ type: 'SET_LOGIN_DATA', name: 'disabled', value: false });
    }
  }, [dispatch]);

  useEffect(() => {
    if (loginState.forgotPasswordOpen) {
      $('#modal').modal('show');

      // Reset modal state when it is hidden
      $('#modal').on('hidden.bs.modal', () => {
        dispatch({ type: 'SET_LOGIN_DATA', name: 'forgotPasswordOpen', value: false });
      });
    }
  }, [loginState.forgotPasswordOpen, dispatch]);

  useEffect(() => {
    checkInitialPath();
    checkRememberedUser();
  }, [checkInitialPath, checkRememberedUser]);

  useEffect(() => {
    const handleScroll = () => {
      const featuresSection = document.querySelector('#features-section');
      if (featuresSection) {
        const rect = featuresSection.getBoundingClientRect();
        setStickyHeaderVisible(rect.top <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      dispatch({ type: 'SET_LOGIN_DATA', name, value });
    },
    [dispatch]
  );

  const handleRememberMeChange = useCallback(() => {
    dispatch({ type: 'SET_LOGIN_DATA', name: 'rememberMe', value: !loginState.rememberMe });
  }, [dispatch, loginState.rememberMe]);

  const handleForgotPasswordClick = useCallback(() => {
    dispatch({ type: 'SET_LOGIN_DATA', name: 'forgotPasswordOpen', value: true });
  }, [dispatch]);

  const handleSubmitForm = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!loginState.username || !loginState.password) {
        showToast('error', 'Username and password are required.', 3000);
        return;
      }

      showToast('success', 'Logging in, please wait ...', 5000);

      localStorage.setItem('username', loginState.username);
      localStorage.setItem('rememberMe', loginState.rememberMe.toString());

      postData(
        '/auth/login',
        { username: loginState.username, password: loginState.password },
        (response) => {
          if (response.status === 200) {
            const storedPath = localStorage.getItem('path');
            localStorage.removeItem('path');

            if (storedPath) {
              window.location.href = `/account${storedPath}`;
            } else if (
              response.data.userDetails.projectId.length > 1 ||
              response.data.userDetails.userRole <= 4
            ) {
              window.location.href = '/account';
            } else {
              window.location.href = `/account#/projects/${response.data.userDetails.projectId[0]}`;
            }
          } else {
            showToast('error', response.data.message, 3000);
          }
        }
      );
    },
    [loginState.username, loginState.password, loginState.rememberMe]
  );

  return (
    <>
      {isStickyHeaderVisible && <StickyHeader />} {/* Render StickyHeader when visible */}
      <div
        id="login-section"
        className={clsx(
          'tw-flex',
          'tw-min-h-screen',
          'tw-bg-cover',
          'tw-bg-center',
          'tw-bg-no-repeat'
        )}
        style={{
          backgroundImage: `url('images/landing-login-hero-image.png')`,
        }}
      >
        <div
          className={clsx(
            'tw-hidden',
            'md:tw-flex',
            'md:tw-w-[70%]',
            'tw-items-center',
            'tw-justify-center',
            'md:tw-pl-24'
          )}
        >
          <div className={clsx('tw-text-white', 'tw-text-left', 'tw-px-12')}>
            <img
              src={'images/cloudcfo-logo-white-icon-beside-text.png'}
              alt="Cloud CFO Logo"
              className="logo-dim"
            />
            <p className={clsx('subheading-text', 'tw-text-2xl', 'tw-text-white')}>
              Stay in control of your finances with real-time access to your records, requests, and
              accounting team—powered by tech, built to help your business grow.
            </p>
          </div>
        </div>
        <div
          className={clsx(
            'tw-flex',
            'tw-w-full',
            'md:tw-flex',
            'md:tw-w-1/2',
            'tw-items-center',
            'tw-justify-end',
            'tw-bg-transparent',
            'tw-px-6',
            'tw-py-12',
            'md:tw-py-12',
            'tw-place-content-end',
            'md:tw-pr-12',
            'md:tw-mr-24'
          )}
        >
          <div
            className={clsx(
              'tw-w-full',
              'tw-bg-white',
              'tw-p-16',
              'tw-rounded-xl',
              'md:tw-max-w-2xl',
              'md:tw-bg-white',
              'md:tw-rounded-3xl',
              'md:tw-p-10',
              'md:tw-py-20',
              'md:tw-px-16'
            )}
          >
            <h2 className={clsx('tw-text-2xl', 'tw-font-semibold', 'tw-text-center', 'tw-mb-6')}>
              Log In
            </h2>
            <form
              onSubmit={handleSubmitForm}
              className={clsx('tw-space-y-4', 'tw-flex', 'tw-flex-col', 'tw-gap-y-3')}
            >
              <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-x-2')}>
                <label
                  htmlFor="username"
                  className={clsx(
                    'tw-block',
                    'tw-text-[14px]',
                    'tw-font-medium',
                    'text-base',
                    '!font-normal',
                    'override-font'
                  )}
                >
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={loginState.username}
                  onChange={handleInputChange}
                  placeholder="Enter username"
                  className={clsx(
                    'tw-mt-1',
                    'tw-block',
                    'tw-w-full',
                    'tw-rounded',
                    'tw-border',
                    'tw-px-3',
                    'tw-py-2',
                    'tw-shadow-sm',
                    'override-font'
                  )}
                />
              </div>
              <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-x-2')}>
                <label
                  htmlFor="password"
                  className={clsx('tw-block', 'tw-text-[14px]', 'tw-font-medium', 'override-font')}
                >
                  Password
                </label>
                <div className={clsx('tw-relative')}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={loginState.password}
                    onChange={handleInputChange}
                    placeholder="Enter password"
                    className={clsx(
                      'tw-mt-1',
                      'tw-block',
                      'tw-w-full',
                      'tw-rounded',
                      'tw-border',
                      'tw-px-3',
                      'tw-py-2',
                      'tw-shadow-sm'
                    )}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className={clsx(
                      'tw-absolute',
                      'tw-inset-y-0',
                      'tw-right-0',
                      'tw-flex',
                      'tw-items-center',
                      'tw-pr-3'
                    )}
                  >
                    <i className="material-icons">
                      {showPassword ? 'visibility' : 'visibility_off'}
                    </i>
                  </button>
                </div>
              </div>
              <div className={clsx('tw-text-left')}>
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className={clsx(
                    'tw-text-[12px]',
                    'tw-text-[#188CD3]',
                    'hover:tw-underline',
                    'tw-justify-self-start'
                  )}
                >
                  Forgot Password?
                </button>
              </div>
              <div className={clsx('tw-flex', 'tw-items-center')}>
                <input
                  type="checkbox"
                  checked={loginState.rememberMe}
                  onChange={handleRememberMeChange}
                  className={clsx('tw-h-5', 'tw-w-5', 'tw-rounded', 'tw-border-gray-300')}
                  style={{
                    marginTop: 0,
                  }}
                />
                <label
                  htmlFor="rememberMe"
                  className={clsx('tw-ml-4', 'tw-text-[14px]', 'tw-mb-0', 'override-font')}
                >
                  Remember Me
                </label>
              </div>
              <button
                type="submit"
                disabled={loginState.disabled}
                className={clsx(
                  'tw-w-full',
                  'tw-bg-[#188CD3]',
                  'tw-text-white',
                  'tw-py-6',
                  'tw-rounded-md',
                  'tw-hover:tw-bg-blue-700'
                )}
              >
                Log In
              </button>
              <div className={clsx('tw-text-xs', 'tw-mt-4', 'tw-flex', 'tw-justify-evenly')}>
                <a
                  href={`/terms-and-conditions`}
                  target="_blank"
                  className={clsx('tw-text-[#188CD3]', 'hover:tw-underline', 'tw-text-[12px]')}
                  rel="noreferrer"
                >
                  Terms and Conditions
                </a>
                <a
                  href={`/privacy-policy`}
                  target="_blank"
                  className={clsx('tw-text-[#188CD3]', 'hover:tw-underline', 'tw-text-[12px]')}
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </div>
            </form>
          </div>
        </div>
        {loginState.forgotPasswordOpen && (
          <ForgotPassword
            type={'client'}
            height={'300px'}
            onClose={() =>
              dispatch({ type: 'SET_LOGIN_DATA', name: 'forgotPasswordOpen', value: false })
            }
          />
        )}
      </div>
      <div id="features-section">
        <FeaturesSection />
      </div>
      <div>
        <BusinessGrowthSection />
      </div>
      <div>
        <BenefitsSection />
      </div>
      <div>
        <Footer />
      </div>
    </>
  );
};

export default Auth;
